import { Link } from "react-router-dom";
import { Droplets, Sun, Scissors, Snowflake, Leaf, Bug, ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

const SECTIONS = [
  {
    title: "Mums",
    intro: "Potted mums are thirsty and love light. With a little attention they'll bloom for six weeks or more.",
    tips: [
      { icon: Droplets, h: "Check the soil every morning", p: "Potted mums dry out fast. Press a finger an inch into the soil — if it's dry, water slowly at the base until it drains from the bottom. In warm early-fall weather that's usually daily; on cool, cloudy days every 2–3 days. Never let them wilt, but don't leave them sitting in a saucer of water." },
      { icon: Sun, h: "Give them 4–6 hours of sun", p: "Morning sun is ideal. If your porch is shaded all day, rotate pots to a sunnier step for a few hours." },
      { icon: Scissors, h: "Pinch spent blooms", p: "Snip faded flowers at the stem to redirect energy into new buds and keep the plant looking full." },
      { icon: Snowflake, h: "Cover on frosty nights", p: "Below 32°F, drape a sheet over them or tuck pots against the house. Uncover in the morning. Mums tolerate a light frost, but a hard freeze will end the blooms." },
    ],
  },
  {
    title: "Pumpkins & gourds",
    intro: "Heirloom pumpkins are hardy, but heat, moisture and hungry critters shorten their life. These habits keep them firm through November.",
    tips: [
      { icon: Sun, h: "Keep them dry and off wet surfaces", p: "Set pumpkins on hay, wood or a doormat rather than damp concrete. Wipe off rain and dew when you can." },
      { icon: Leaf, h: "Avoid carving", p: "Uncarved pumpkins last 8–12 weeks; carved ones only days. Paint or vinyl is a longer-lasting way to decorate." },
      { icon: Bug, h: "Deter squirrels and deer", p: "A light spritz of diluted vinegar or a dab of peppermint oil on the stem end helps. Reapply after rain." },
      { icon: Snowflake, h: "Mind the freeze–thaw", p: "Repeated hard freezes soften pumpkins. On very cold nights, tuck the smaller ones under cover and set them back out in the morning." },
    ],
  },
  {
    title: "Hay bales & corn stalks",
    intro: "Rustic accents need the least care — just keep them dry and secure.",
    tips: [
      { icon: Droplets, h: "Shield from soaking rain", p: "Wet hay molds and heats up. If a storm is coming, move bales under the eave or cover with a tarp." },
      { icon: Leaf, h: "Tie stalks to a post", p: "Twine or jute around a railing keeps bundles upright in Utah wind. Re-tighten after gusty days." },
    ],
  },
];

export default function Care() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <section className="pp-container py-16 sm:py-20">
        <div className="max-w-3xl">
          <div className="pp-caption mb-3">Care guide</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl font-light leading-tight" style={{ color: "var(--pp-burgundy)" }}>
            Care for your setup.
          </h1>
          <p className="mt-5 text-base sm:text-lg leading-relaxed" style={{ color: "var(--pp-text-muted)" }}>
            Everything we place on your porch is yours to keep. A few minutes a week will keep the mums blooming and the pumpkins firm all the way to Thanksgiving.
          </p>
        </div>
      </section>

      {SECTIONS.map((s, i) => (
        <section key={s.title} className={i % 2 === 0 ? "pp-grain border-t" : "border-t"} style={{ backgroundColor: i % 2 === 0 ? "var(--pp-parchment)" : "var(--pp-cream)", borderColor: "var(--pp-border)" }} data-testid={`care-section-${s.title.split(" ")[0].toLowerCase()}`}>
          <div className="pp-container py-16 sm:py-20 relative grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <h2 className="font-serif-display text-4xl" style={{ color: "var(--pp-burgundy)" }}>{s.title}</h2>
              <p className="mt-3 leading-relaxed" style={{ color: "var(--pp-text-muted)" }}>{s.intro}</p>
            </div>
            <ul className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
              {s.tips.map((t) => (
                <li key={t.h} className="border-t pt-4" style={{ borderColor: "var(--pp-burgundy)" }}>
                  <t.icon className="h-5 w-5 mb-2" style={{ color: "var(--pp-terracotta)" }} />
                  <h3 className="font-medium" style={{ color: "var(--pp-text)" }}>{t.h}</h3>
                  <p className="mt-1 text-sm leading-relaxed" style={{ color: "var(--pp-text-muted)" }}>{t.p}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ))}

      <section className="pp-container py-16 text-center">
        <p className="mb-4" style={{ color: "var(--pp-text-muted)" }}>Questions about your display? We're a message away at <a href="mailto:pumpkinandporchhelp@gmail.com" className="underline underline-offset-4" style={{ color: "var(--pp-burgundy)" }}>pumpkinandporchhelp@gmail.com</a>.</p>
        <Link to="/" data-testid="care-cta" className="pp-btn">Book a porch <ArrowRight className="h-4 w-4" /></Link>
      </section>
      <div className="flex-1" />
      <Footer />
    </div>
  );
}
